// `GET /api/v1/changes/{id}/audit`: the audit of the write (#262, C5; the mock's screen 5).
//
// Read off `control_plane/routes/changes.py::read_change_audit` and
// `actions/audit.py`. One answer carries the five things the screen names: the
// patch (the `request_action` payload's `resource_change`), the eligibility
// ruling and every later phase (the platform's rows for the change, in log
// order), the attribution the operator session wrote, both whole resource
// documents (`change.verification`, never on the log), and the outcome.

import { browserApi, type Api } from "./http";

export const changeAuditPath = (changeId: string): string =>
  `/api/v1/changes/${encodeURIComponent(changeId)}/audit`;

export interface AuditPhase {
  phase: string;
  type: string;
  seq: number;
  appended_at: string | null;
  payload: Record<string, unknown>;
}

/** One side of `change.verification`: the whole document, or why it is not there. */
export interface StoredDocument {
  status: "captured" | "too_large" | "unreadable";
  document?: Record<string, unknown>;
  bytes?: number;
  reason?: string;
}

/** Who approved, with which factor, under which authority (the mock's screen 5). */
export interface AuditApproval {
  approver: { user_id: string; email: string | null; display_name: string | null } | null;
  approved_at: string | null;
  authority: "workspace" | "grant" | null;
  grant_id: string | null;
  /** The step-up method the approval was proved with, from its audit row; null if none. */
  step_up_method: string | null;
  assurance_id: string | null;
}

export interface ChangeAudit {
  change_id: string;
  run_id: string;
  status: string;
  waiting_for_sweep: boolean;
  slow: boolean;
  sweep_period_seconds: number | null;
  request: Record<string, unknown>;
  executed_at: string | null;
  attribution: Record<string, unknown> | null;
  verification: { before: StoredDocument; after: StoredDocument; roots: string[] } | null;
  outcome_due_at: string | null;
  outcome_observed_at: string | null;
  approval: AuditApproval;
  phases: AuditPhase[];
}

const record = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const textOrNull = (value: unknown): string | null => (typeof value === "string" ? value : null);

function storedDocument(value: unknown): StoredDocument {
  const row = record(value);
  const status =
    row.status === "captured" || row.status === "too_large" ? row.status : "unreadable";
  return {
    status,
    ...(status === "captured" ? { document: record(row.document) } : {}),
    ...(typeof row.bytes === "number" ? { bytes: row.bytes } : {}),
    ...(typeof row.reason === "string" ? { reason: row.reason } : {}),
  };
}

function auditApproval(value: unknown): AuditApproval {
  const row = record(value);
  const approver = row.approver === null || row.approver === undefined ? null : record(row.approver);
  return {
    approver:
      approver === null
        ? null
        : {
            user_id: String(approver.user_id ?? ""),
            email: textOrNull(approver.email),
            display_name: textOrNull(approver.display_name),
          },
    approved_at: textOrNull(row.approved_at),
    authority: row.authority === "workspace" || row.authority === "grant" ? row.authority : null,
    grant_id: textOrNull(row.grant_id),
    step_up_method: textOrNull(row.step_up_method),
    assurance_id: textOrNull(row.assurance_id),
  };
}

export function changeAuditFrom(body: unknown): ChangeAudit {
  const row = record(body);
  const verification = row.verification === null ? null : record(row.verification);
  return {
    change_id: String(row.change_id ?? ""),
    run_id: String(row.run_id ?? ""),
    status: String(row.status ?? ""),
    waiting_for_sweep: row.waiting_for_sweep === true,
    slow: row.slow === true,
    sweep_period_seconds: typeof row.sweep_period_seconds === "number" ? row.sweep_period_seconds : null,
    request: record(row.request),
    executed_at: textOrNull(row.executed_at),
    attribution: row.attribution === null ? null : record(row.attribution),
    verification:
      verification === null
        ? null
        : {
            before: storedDocument(verification.before),
            after: storedDocument(verification.after),
            roots: Array.isArray(verification.roots) ? verification.roots.map(String) : [],
          },
    outcome_due_at: textOrNull(row.outcome_due_at),
    outcome_observed_at: textOrNull(row.outcome_observed_at),
    approval: auditApproval(row.approval),
    phases: Array.isArray(row.phases)
      ? row.phases.map((value) => {
          const p = record(value);
          return {
            phase: String(p.phase ?? ""),
            type: String(p.type ?? ""),
            seq: typeof p.seq === "number" ? p.seq : 0,
            appended_at: textOrNull(p.appended_at),
            payload: record(p.payload),
          };
        })
      : [],
  };
}

export async function readChangeAudit(changeId: string, api: Api = browserApi): Promise<ChangeAudit> {
  return changeAuditFrom(await api.get<unknown>(changeAuditPath(changeId)));
}

/** One property that differs between the two whole documents. */
export interface DiffRow {
  property: string;
  before: unknown;
  after: unknown;
  /** The patch named this root property; otherwise the write moved it collaterally. */
  patched: boolean;
}

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);

/**
 * The whole-resource diff: every root property whose value differs, the patched
 * ones first. A property absent on one side is `undefined` there, never `null`.
 */
export function wholeDocumentDiff(
  before: Record<string, unknown>,
  after: Record<string, unknown>,
  patched: readonly string[],
): DiffRow[] {
  const names = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return names
    .filter((name) => !(name in before && name in after && same(before[name], after[name])))
    .map((name) => ({
      property: name,
      before: before[name],
      after: after[name],
      patched: patched.includes(name),
    }))
    .sort((a, b) => Number(b.patched) - Number(a.patched) || a.property.localeCompare(b.property));
}
